import { QueryClient, UseMutationResult } from 'react-query';
import HttpServiceHelper from './httpServiceHelper';
import { createFeatureApi, getFeatureListApi, updateFeatureApi, syncFeaturesApi, getSourceListApi } from '../constants/apiEndPoints';
import { _HttpMethods, _Payload } from '../constants/staticTypes';
import * as Yup from 'yup';
import { AxiosResponse } from 'axios';
import { getErrorMessage } from '../utils/common';
import { AlertVariant } from '../constants/constants';

export type _FeatureForm = {
    id?: string;
    name: string;
    lookupKey: string;
    description: string;
    isAddon: boolean;
    sourceType: string; // "ORGANIZATION" or "TENANT"
    sourceId: string;
    isActive: boolean;
    metadata?: Record<string, any>;
};

export type _Feature = {
    id: string;
    featureKey: string;
    name: string;
    description: string | null;
    isAddon: boolean;
    sourceType: string | null;
    sourceId: string | null;
    metadata: Record<string, any>;
    isActive: boolean;
    lookupKey: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: string | null;
    featureOptions?: Array<{
        id: string;
        featureId: string;
        optionName: string;
        optionDescription: string;
        optionUnitList: string[];
        metadata: Record<string, any>;
        createdAt: string;
        updatedAt: string;
        deletedAt: string | null;
    }>;
    associatedPlans?: Array<any>;
    sourceModule?: {
        id: string;
        moduleName: string;
        moduleDescription: string;
        isDefault: boolean;
        deletedBy: string | null;
        createdAt: string;
        updatedAt: string;
        deletedAt: string | null;
    };
};

export const initialFeatureFormData: _FeatureForm = {
    name: '',
    lookupKey: '',
    description: '',
    isAddon: false,
    sourceType: 'ORGANIZATION',
    sourceId: '',
    isActive: true,
    metadata: {},
};

export const convertFeatureData = (featureData: _Feature | undefined): _FeatureForm | null => {
    if (!featureData) return null;

    return {
        id: featureData.id ?? '',
        name: featureData.name ?? '',
        lookupKey: featureData.lookupKey ?? '',
        description: featureData.description ?? '',
        isAddon: featureData.isAddon ?? false,
        sourceType: featureData.sourceType ?? 'ORGANIZATION',
        sourceId: featureData.sourceId ?? '',
        isActive: featureData.isActive ?? true,
        metadata: featureData.metadata ?? {},
    };
};

export const FeatureFormSchema = Yup.object().shape({
    name: Yup.string().required('Feature Name is required'),
    lookupKey: Yup.string().required('Lookup Key is required'),
    description: Yup.string().required('Description is required'),
    sourceType: Yup.string().required('Scope is required'),
    sourceId: Yup.string().required('Module is required'),
});

export const getFeatureList = (queryClient: QueryClient, page: number, limit: number, isActive?: boolean, search?: string) => {
    let url = `${getFeatureListApi}?limit=${limit}&page=${page}`;

    if (isActive !== undefined) {
        url += `&filters[isActive]=${isActive}`;
    }

    if (search) {
        url += search;
    }

    return HttpServiceHelper({
        url,
        method: _HttpMethods.GET,
        queryClient,
    });
};

export const getSourceList = (queryClient: QueryClient) => {
    return HttpServiceHelper({
        url: getSourceListApi,
        method: _HttpMethods.GET,
        queryClient,
    });
};

export const createFeature = (
    createFeatureMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _FeatureForm,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    createFeatureMutation.mutate(
        {
            url: createFeatureApi,
            method: _HttpMethods.POST,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const updateFeature = (
    updateFeatureMutation: UseMutationResult<AxiosResponse<any, any>, unknown, _Payload, unknown>,
    param: _FeatureForm,
    onSuccess: (data: any) => void,
    onError: (res: string, variant: string) => void
) => {
    updateFeatureMutation.mutate(
        {
            url: updateFeatureApi,
            method: _HttpMethods.PUT,
            data: param,
        },
        {
            onSuccess: (res) => onSuccess(res.data),
            onError: (res) => onError(getErrorMessage(res), AlertVariant.ERROR),
        }
    );
};

export const syncFeaturesFromStripe = (queryClient: QueryClient) => {
    return HttpServiceHelper({
        url: syncFeaturesApi,
        method: _HttpMethods.GET,
        queryClient,
    });
};
